import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as ReactLeaflet from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { PageBody } from '../../../elements/page-body';
import { useHttpClient } from '../../../shared/http-client';
import { useDrawer } from '../../../shared/drawer';
import { Area } from './types';
import { AreaForm } from './areas-form';
import { AreasList } from './areas-list';
import { getAreaColor } from './color-utils';

export const Areas: React.FC = () => {
    const { t } = useTranslation() as { t: (key: string) => string };
    const { api, state } = useHttpClient();
    const { open, close } = useDrawer();

    const [areas, setAreas] = useState<Area[]>([]);
    const [showAreas, setShowAreas] = useState<boolean>(true);
    const [editingArea, setEditingArea] = useState<Area | null>(null);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [tempCircle, setTempCircle] = useState<{
        lat: number;
        lng: number;
        radius: number;
    } | null>(null);
    const [selectedRadius, setSelectedRadius] = useState<number>(10);

    const MapContainerAny = (ReactLeaflet as any).MapContainer;
    const TileLayerAny = (ReactLeaflet as any).TileLayer;
    const CircleAny = (ReactLeaflet as any).Circle;
    const useMapEvents = (ReactLeaflet as any).useMapEvents;

    const defaultCenter: [number, number] = [40.416775, -3.703790];

    useEffect(() => {
        loadAreas();
    }, []);

    useEffect(() => {
        if (state.path?.includes('/fleet/areas/') && state.method === 'GET' && state.data) {
            if (Array.isArray(state.data.results)) {
                setAreas(state.data.results);
            }
        }

        if (state.path?.includes('/fleet/areas/') && (state.method === 'POST' || state.method === 'PATCH')) {
            if (state.data) {
                toast.success(t('common.message.saved'));
                loadAreas();
                setIsCreating(false);
                setEditingArea(null);
                setTempCircle(null);
                close();
            }
        }

        if (state.path?.includes('/fleet/areas/') && state.method === 'DELETE') {
            if (state.data || state.data === null) {
                toast.success(t('common.message.deleted'));
                loadAreas();
            }
        }

        if (state.error) {
            toast.error(t('common.message.error'));
        }
    }, [state]);

    const loadAreas = () => {
        api('/fleet/areas/', 'GET');
    };

    const handleMapClick = (lat: number, lng: number) => {
        if (isCreating) {
            setTempCircle({ lat, lng, radius: selectedRadius });
        }
    };

    const handleSaveTempArea = () => {
        if (tempCircle) {
            open(
                t('common.label.nueva-area'),
                <AreaForm
                    area={{
                        center_lat: tempCircle.lat,
                        center_lon: tempCircle.lng,
                        radius: tempCircle.radius,
                        name: '',
                        description: ''
                    }}
                    onSave={saveArea}
                    onCancel={() => {
                        setTempCircle(null);
                        setIsCreating(false);
                        close();
                    }}
                />
            );
        }
    };

    const saveArea = (values: any) => {
        if (values.id) {
            api(`/fleet/areas/${values.id}/`, 'PATCH', values);
        } else {
            api('/fleet/areas/', 'POST', values);
        }
    };

    const handleEdit = (area: Area) => {
        setEditingArea(area);
        open(
            t('common.label.editar-area'),
            <AreaForm
                area={area}
                onSave={saveArea}
                onCancel={() => {
                    setEditingArea(null);
                    close();
                }}
                onRadiusChange={(newRadius: number) => {
                    // Update the area in real-time while editing
                    setEditingArea(prev => prev ? { ...prev, radius: newRadius } : null);
                    // Also update the areas array to reflect the change on the map
                    setAreas(prev => prev.map(a => 
                        a.id === area.id ? { ...a, radius: newRadius } : a
                    ));
                }}
            />
        );
    };

    const handleDelete = (id: number) => {
        if (window.confirm(t('common.modal.validate-delete'))) {
            api(`/fleet/areas/${id}/`, 'DELETE');
        }
    };

    const MapClickHandler = () => {
        useMapEvents({
            click: (e: any) => {
                handleMapClick(e.latlng.lat, e.latlng.lng);
            }
        });
        return null;
    };

    
    return (
        <PageBody title={'common.label.areas'} noMargin={true}>
            <div className="w-full flex flex-col p-4" style={{ height: 'calc(100vh - 120px)' }}>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4 flex-shrink-0">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setIsCreating(!isCreating)}
                                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                    isCreating
                                        ? 'bg-red-500 hover:bg-red-600 text-white'
                                        : 'bg-[#006bc6] hover:bg-[#0056a3] text-white'
                                }`}
                            >
                                {isCreating ? t('common.button.cancel') : t('common.label.nueva-area')}
                            </button>

                            {isCreating && (
                                <>
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium text-gray-700">
                                            {t('common.label.radio')} (km):
                                        </label>
                                        <input
                                            type="range"
                                            min="1"
                                            max="500"
                                            step="1"
                                            value={selectedRadius}
                                            onChange={(e) => {
                                                setSelectedRadius(parseFloat(e.target.value));
                                                if (tempCircle) {
                                                    setTempCircle({ ...tempCircle, radius: parseFloat(e.target.value) });
                                                }
                                            }}
                                            className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                            style={{
                                                background: `linear-gradient(to right, #006bc6 0%, #006bc6 ${((selectedRadius - 1) / 499) * 100}%, #e5e7eb ${((selectedRadius - 1) / 499) * 100}%, #e5e7eb 100%)`
                                            }}
                                        />
                                        <span className="text-sm font-semibold text-[#006bc6] min-w-[40px]">
                                            {selectedRadius} km
                                        </span>
                                    </div>

                                    {tempCircle && (
                                        <button
                                            onClick={handleSaveTempArea}
                                            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all"
                                        >
                                            {t('common.button.save')}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="showAreas"
                                checked={showAreas}
                                onChange={(e) => setShowAreas(e.target.checked)}
                                className="w-4 h-4 text-[#006bc6] border-gray-300 rounded focus:ring-[#006bc6]"
                            />
                            <label htmlFor="showAreas" className="text-sm font-medium text-gray-700 cursor-pointer">
                                {t('common.label.mostrar-areas')}
                            </label>
                            <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-semibold">
                                {areas.length} {t('common.label.areas')}
                            </span>
                        </div>
                    </div>

                    {isCreating && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-800">
                                <span className="font-semibold">💡 {t('common.label.instrucciones')}:</span>{' '}
                                {t('common.label.click-mapa-crear-area')}
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex-1 flex gap-4 min-h-0">
                    <div className="flex-1 rounded-lg overflow-hidden shadow-lg border border-gray-200">
                        <MapContainerAny
                            center={defaultCenter}
                            zoom={6}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={true}
                        >
                            <TileLayerAny
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />

                            {showAreas &&
                                areas.map((area) => (
                                    <CircleAny
                                        key={area.id}
                                        center={[area.center_lat, area.center_lon]}
                                        radius={area.radius * 1000}
                                        pathOptions={{
                                            color: getAreaColor(area.id),
                                            fillColor: getAreaColor(area.id),
                                            fillOpacity: 0.2,
                                            weight: 3
                                        }}
                                    >
                                        <ReactLeaflet.Popup>
                                            <div className="p-2 min-w-[200px]">
                                                <h3 className="font-bold text-lg mb-2" style={{ color: getAreaColor(area.id) }}>
                                                    {area.name}
                                                </h3>
                                                {area.description && (
                                                    <p className="text-sm text-gray-600 mb-2">{area.description}</p>
                                                )}
                                                <p className="text-xs text-gray-500 mb-3">
                                                    {t('common.label.radio')}: {area.radius} km
                                                </p>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleEdit(area)}
                                                        className="px-3 py-1 bg-[#006bc6] text-white text-xs rounded hover:bg-[#0056a3] transition-all"
                                                    >
                                                        {t('common.label.edit')}
                                                    </button>
                                                    <button
                                                        onClick={() => area.id && handleDelete(area.id)}
                                                        className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-all"
                                                    >
                                                        {t('common.label.delete')}
                                                    </button>
                                                </div>
                                            </div>
                                        </ReactLeaflet.Popup>
                                    </CircleAny>
                                ))}

                            {tempCircle && (
                                <CircleAny
                                    center={[tempCircle.lat, tempCircle.lng]}
                                    radius={tempCircle.radius * 1000}
                                    pathOptions={{
                                        color: '#10b981',
                                        fillColor: '#10b981',
                                        fillOpacity: 0.3,
                                        weight: 3,
                                        dashArray: '10, 10'
                                    }}
                                />
                            )}

                            {isCreating && <MapClickHandler />}
                        </MapContainerAny>
                    </div>

                    <div className="w-80 flex-shrink-0">
                        <AreasList areas={areas} onEdit={handleEdit} onDelete={handleDelete} />
                    </div>
                </div>
            </div>
        </PageBody>
    );
};
